import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => nav("/login", { replace: true }), 2000);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col" data-testid="reset-password-page">
      <div className="p-6">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 font-bold text-sm px-4 py-2 border-2 border-[#0A0A0A] rounded-full bg-white hover:shadow-brutal-sm transition-shadow"
        >
          <ArrowLeft size={16} /> Back to Login
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 pb-16">
        <div className="w-full max-w-md brutal-card p-8 md:p-10 bg-white">
          <div className="mb-6 flex items-center gap-3">
            <div className="p-3 border-2 border-[#0A0A0A] rounded-xl bg-[#00E5FF]">
              <KeyRound size={22} strokeWidth={2.5} />
            </div>
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em]">Almost there</span>
              <h1 className="font-heading font-black text-3xl mt-1 tracking-tighter">Set a new password</h1>
            </div>
          </div>

          {done ? (
            <div className="p-5 border-2 border-[#0A0A0A] rounded-xl bg-[#FFE600] flex items-center gap-3" data-testid="reset-done">
              <CheckCircle2 size={22} strokeWidth={2.5} />
              <div>
                <div className="font-heading font-black text-xl tracking-tighter">Password updated</div>
                <div className="text-sm font-medium mt-0.5">Redirecting you to login...</div>
              </div>
            </div>
          ) : !token ? (
            <div
              className="p-4 border-2 border-[#0A0A0A] rounded-xl bg-[#FF4D4D] text-white font-bold"
              data-testid="reset-missing-token"
            >
              Missing reset token. Please use the link from your Forgot Password email.
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="reset-password-form">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest mb-2">New password</label>
                <input
                  data-testid="reset-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-white font-medium focus:outline-none focus:ring-2 focus:ring-[#0A0A0A] focus:ring-offset-2"
                  placeholder="At least 6 characters"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest mb-2">Confirm password</label>
                <input
                  data-testid="reset-confirm-input"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={6}
                  className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-white font-medium focus:outline-none focus:ring-2 focus:ring-[#0A0A0A] focus:ring-offset-2"
                  placeholder="Re-enter password"
                />
              </div>
              {error && (
                <div
                  data-testid="reset-error"
                  className="bg-[#FF4D4D] text-white border-2 border-[#0A0A0A] rounded-xl px-4 py-2 text-sm font-bold"
                >
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="reset-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 font-bold px-5 py-3 border-2 border-[#0A0A0A] rounded-full bg-[#00CC66] text-white shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform disabled:opacity-60"
              >
                {loading ? "Saving..." : "Update password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
